import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { BlurView } from 'expo-blur';
import LoadingButton from './LoadingButton';
import { requestPasswordReset } from '../services/password-reset-service';

interface ForgotPasswordModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  visible,
  onClose,
  onSuccess,
  onError,
}) => {
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailError, setForgotEmailError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    setForgotEmailError('');
    setForgotResult(null);

    if (!forgotEmail) {
      setForgotEmailError('Please enter your email address.');
      setForgotLoading(false);
      return;
    }

    if (!validateEmail(forgotEmail)) {
      setForgotEmailError('Please enter a valid email address.');
      setForgotLoading(false);
      return;
    }

    try {
      const resp = await requestPasswordReset(forgotEmail);
      const result = {
        message: resp.payload?.message || 'Unknown error',
        success: resp.status === 200,
      };

      setForgotResult(result);

      if (result.success) {
        onSuccess?.(result.message);
      } else {
        onError?.(result.message);
      }
    } catch (e) {
      const errorMessage = 'An error occurred. Please try again.';
      setForgotResult({
        message: errorMessage,
        success: false,
      });
      onError?.(errorMessage);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleClose = () => {
    setForgotEmail('');
    setForgotEmailError('');
    setForgotResult(null);
    onClose();
  };

  if (!visible) return null;

  return (
    <View style={styles.overlay}>
      <BlurView intensity={20} style={styles.blurContainer}>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Forgot Your Password?</Text>
            <Text style={styles.modalSubtitle}>
              Enter your email address and we'll send you a link to reset your
              password.
            </Text>

            <TextInput
              style={styles.emailInput}
              placeholder="Enter your email"
              placeholderTextColor="#8D8E90"
              value={forgotEmail}
              onChangeText={setForgotEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
            />

            {forgotEmailError ? (
              <Text style={styles.errorText}>{forgotEmailError}</Text>
            ) : null}

            {forgotResult && (
              <Text
                style={[
                  styles.resultText,
                  forgotResult.success ? styles.successText : styles.errorText,
                ]}
              >
                {forgotResult.message}
              </Text>
            )}

            <View style={styles.buttonContainer}>
              <LoadingButton
                onPress={handleForgotPassword}
                disabled={forgotLoading}
                style={styles.submitButton}
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </LoadingButton>

              <Pressable onPress={handleClose} style={styles.cancelButton}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </BlurView>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  blurContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#23262B',
    borderRadius: 16,
    padding: 24,
    width: 350,
    maxWidth: '90%',
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
  },
  modalContent: {
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 8,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#8D8E90',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  emailInput: {
    width: '100%',
    backgroundColor: '#1A1D21',
    borderRadius: 8,
    padding: 12,
    color: 'white',
    fontSize: 16,
    marginBottom: 8,
  },
  errorText: {
    color: '#F97777',
    fontSize: 14,
    marginBottom: 8,
    textAlign: 'center',
  },
  successText: {
    color: '#51B868',
    fontSize: 14,
    marginBottom: 8,
    textAlign: 'center',
  },
  resultText: {
    fontSize: 14,
    marginBottom: 16,
    textAlign: 'center',
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
  },
  submitButton: {
    width: '100%',
  },
  cancelButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#8D8E90',
    fontSize: 16,
    fontWeight: '500',
  },
});

export default ForgotPasswordModal;
